import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

/* Zuschneide-Werkzeug (Coach-Cockpit W1.7): Der Trainer zieht auf dem Fortschrittsfoto ein Rechteck auf — Kopf außerhalb.
   Maus, Stift und Finger (Pointer-Events), Werte in Prozent. Gerendert und verschlüsselt gespeichert wird auf dem Server;
   das Mitglied gibt den Ausschnitt danach in der App frei. */

const MIN = 10;
const START = { x: 0, y: 30, w: 100, h: 70 };
const grenze = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

export default function TestimonialZuschnitt({ laden, onSpeichern, onAbbrechen }) {
  const [url, setUrl] = useState(null);
  const [fehler, setFehler] = useState('');
  const [r, setR] = useState(START);
  const [kopf, setKopf] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const flaeche = useRef(null);
  const zug = useRef(null);

  useEffect(() => {
    let weg = null;
    let aktiv = true;
    laden().then((res) => { if (aktiv) { weg = URL.createObjectURL(res.data); setUrl(weg); } })
      .catch(() => setFehler('Foto nicht geladen'));
    return () => { aktiv = false; if (weg) URL.revokeObjectURL(weg); };
  }, [laden]);

  const prozent = (e) => {
    const b = flaeche.current.getBoundingClientRect();
    return { x: grenze(((e.clientX - b.left) / b.width) * 100, 0, 100), y: grenze(((e.clientY - b.top) / b.height) * 100, 0, 100) };
  };
  const runter = (e) => {
    e.preventDefault();
    flaeche.current.setPointerCapture?.(e.pointerId);
    zug.current = prozent(e);
  };
  const ziehen = (e) => {
    if (!zug.current) return;
    const a = zug.current;
    const p = prozent(e);
    setR({ x: Math.min(a.x, p.x), y: Math.min(a.y, p.y), w: Math.abs(p.x - a.x), h: Math.abs(p.y - a.y) });
  };
  const hoch = () => { zug.current = null; };
  const gross = r.w >= MIN && r.h >= MIN;
  const speichern = async () => {
    setLaeuft(true);
    try {
      await onSpeichern({ x: +r.x.toFixed(2), y: +r.y.toFixed(2), w: +r.w.toFixed(2), h: +r.h.toFixed(2) }, kopf);
    } finally { setLaeuft(false); }
  };

  return (
    <div className="space-y-3" data-testid="testimonial-zuschnitt">
      <p className="text-sm text-muted-foreground">Rechteck aufziehen: Der Kopf muss <strong>außerhalb</strong> liegen. Nur der helle Bereich wird verwendet.</p>
      {fehler && <p className="text-sm text-destructive" role="alert">{fehler}</p>}
      {!url && !fehler && <Skeleton className="aspect-[3/4] w-full max-w-sm rounded-md" />}
      {url && (
        <div ref={flaeche} className="relative w-full max-w-sm touch-none select-none overflow-hidden rounded-md"
          onPointerDown={runter} onPointerMove={ziehen} onPointerUp={hoch} onPointerCancel={hoch}
          role="application" aria-label="Ausschnitt wählen">
          <img src={url} alt="Fortschrittsfoto" className="pointer-events-none block w-full" draggable={false} />
          <div className="pointer-events-none absolute border-2 border-marke"
            style={{ left: `${r.x}%`, top: `${r.y}%`, width: `${r.w}%`, height: `${r.h}%`, boxShadow: '0 0 0 9999px rgba(0,0,0,0.6)' }} />
        </div>
      )}
      <label className="flex min-h-11 items-center gap-3 text-sm font-medium">
        <input type="checkbox" className="h-5 w-5" checked={kopf} onChange={(e) => setKopf(e.target.checked)} data-testid="testimonial-kopf" />
        Der Kopf liegt außerhalb des Ausschnitts (kein Gesicht, keine Tattoos/Merkmale, die direkt erkennen lassen)
      </label>
      {!gross && <p className="text-xs text-destructive">Ausschnitt mindestens {MIN} % breit und hoch.</p>}
      <div className="flex flex-wrap gap-2">
        <Button className="min-h-11" onClick={speichern} disabled={!url || !kopf || !gross || laeuft} data-testid="testimonial-zuschnitt-speichern">
          {laeuft && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}Zuschnitt speichern
        </Button>
        <Button variant="ghost" className="min-h-11" onClick={onAbbrechen} disabled={laeuft}>Abbrechen</Button>
      </div>
    </div>
  );
}
